import * as React from 'react';
import { cn } from '@/lib/utils';

export interface FlexProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  justify?: 'start' | 'end' | 'center' | 'between' | 'around' | 'evenly';
  align?: 'start' | 'end' | 'center' | 'baseline' | 'stretch';
  gap?:
    | 0
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 8
    | 10
    | 12
    | 16
    | 20
    | 24
    | 32
    | 40
    | 48
    | 56
    | 64
    | string;
}

const Flex = React.forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      className,
      direction = 'row',
      wrap = 'nowrap',
      justify = 'start',
      align = 'start',
      gap,
      ...props
    },
    ref
  ) => {
    const directionClasses = {
      row: 'flex-row',
      column: 'flex-col',
      'row-reverse': 'flex-row-reverse',
      'column-reverse': 'flex-col-reverse',
    };

    const wrapClasses = {
      nowrap: 'flex-nowrap',
      wrap: 'flex-wrap',
      'wrap-reverse': 'flex-wrap-reverse',
    };

    const justifyClasses = {
      start: 'justify-start',
      end: 'justify-end',
      center: 'justify-center',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    };

    const alignClasses = {
      start: 'items-start',
      end: 'items-end',
      center: 'items-center',
      baseline: 'items-baseline',
      stretch: 'items-stretch',
    };

    const gapClasses =
      typeof gap === 'string' ? gap : gap !== undefined ? `gap-${gap}` : '';

    return (
      <div
        className={cn(
          'flex',
          directionClasses[direction],
          wrapClasses[wrap],
          justifyClasses[justify],
          alignClasses[align],
          gapClasses,
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Flex.displayName = 'Flex';

type StackProps = Omit<FlexProps, 'direction'>;

// VStack defaults to align="center" to match Chakra's behavior
const VStack = React.forwardRef<HTMLDivElement, StackProps>(
  ({ align = 'center', ...props }, ref) => (
    <Flex ref={ref} direction="column" align={align} {...props} />
  )
);
VStack.displayName = 'VStack';

// HStack defaults to align="center" to match Chakra's behavior
const HStack = React.forwardRef<HTMLDivElement, StackProps>(
  ({ align = 'center', ...props }, ref) => (
    <Flex ref={ref} direction="row" align={align} {...props} />
  )
);
HStack.displayName = 'HStack';

const Spacer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex-1', className)} {...props} />
));
Spacer.displayName = 'Spacer';

export { Flex, VStack, HStack, Spacer };
